/**
 * auditStyles.js — Minimal, compact design system for Audit module
 * Matches RIU.ai aesthetic: clean whites, subtle borders, blue accent
 */

export const injectAuditStyles = () => {
  if (document.getElementById("audit-system-styles")) return;

  const s = document.createElement("style");
  s.id = "audit-system-styles";
  s.textContent = `
    *, *::before, *::after { box-sizing: border-box; }

    :root {
      --ink:       #111827;
      --ink-2:     #374151;
      --ink-3:     #6b7280;
      --ink-4:     #9ca3af;
      --rule:      #e5e7eb;
      --rule-2:    #f3f4f6;
      --surface:   #ffffff;
      --surface-2: #f9fafb;
      --surface-3: #f3f4f6;
      --accent:    #2563eb;
      --accent-2:  #dbeafe;
      --accent-3:  #eff6ff;
      --green:     #16a34a;
      --green-2:   #f0fdf4;
      --green-b:   #bbf7d0;
      --amber:     #d97706;
      --amber-2:   #fffbeb;
      --amber-b:   #fde68a;
      --red:       #dc2626;
      --red-2:     #fef2f2;
      --red-b:     #fecaca;
      --purple:    #7c3aed;
      --purple-2:  #f5f3ff;
      --purple-b:  #ddd6fe;
      --font-sans: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      --font-mono: 'JetBrains Mono', 'Fira Code', monospace;
    }

    /* ── Base inputs ── */
    .audit-input {
      width: 100%; padding: 7px 10px;
      border: 1px solid var(--rule); border-radius: 6px;
      font-size: 13px; font-family: var(--font-sans);
      color: var(--ink); background: var(--surface);
      outline: none; transition: border-color .15s, box-shadow .15s;
    }
    .audit-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(37,99,235,.08); }
    .audit-input::placeholder { color: var(--ink-4); }

    .audit-label {
      display: block; font-size: 10px; font-weight: 600;
      color: var(--ink-3); text-transform: uppercase;
      letter-spacing: .07em; margin-bottom: 5px;
    }

    /* ── Buttons ── */
    .audit-btn-primary {
      padding: 7px 16px; background: var(--accent); color: #fff;
      border: none; border-radius: 6px; font-size: 12.5px; font-weight: 600;
      font-family: var(--font-sans); cursor: pointer; transition: all .15s;
    }
    .audit-btn-primary:hover { background: #1d4ed8; }
    .audit-btn-primary:disabled { opacity: .45; cursor: not-allowed; }

    .audit-btn-secondary {
      padding: 7px 14px; background: var(--surface); color: var(--ink-2);
      border: 1px solid var(--rule); border-radius: 6px; font-size: 12.5px;
      font-weight: 500; font-family: var(--font-sans); cursor: pointer; transition: all .15s;
    }
    .audit-btn-secondary:hover { background: var(--surface-2); border-color: #d1d5db; }
    .audit-btn-secondary:disabled { opacity: .45; cursor: not-allowed; }

    .audit-btn-approve {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 5px 12px; border-radius: 6px; font-size: 12px;
      font-weight: 600; font-family: var(--font-sans); cursor: pointer;
      border: 1px solid var(--green-b); background: var(--green-2); color: var(--green);
      transition: all .15s; white-space: nowrap;
    }
    .audit-btn-approve:hover { background: var(--green-b); }
    .audit-btn-approve:disabled { opacity: .45; cursor: not-allowed; }

    .audit-btn-reject {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 5px 12px; border-radius: 6px; font-size: 12px;
      font-weight: 600; font-family: var(--font-sans); cursor: pointer;
      border: 1px solid var(--red-b); background: var(--red-2); color: var(--red);
      transition: all .15s; white-space: nowrap;
    }
    .audit-btn-reject:hover { background: var(--red-b); }
    .audit-btn-reject:disabled { opacity: .45; cursor: not-allowed; }

    .audit-btn-ghost {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 5px 11px; border-radius: 6px; font-size: 12px;
      font-weight: 500; font-family: var(--font-sans); cursor: pointer;
      border: 1px solid var(--rule); background: transparent; color: var(--ink-3);
      transition: all .15s;
    }
    .audit-btn-ghost:hover { background: var(--surface-2); color: var(--ink-2); }

    /* ── Checkbox ── */
    .audit-checkbox {
      width: 15px; height: 15px; border: 1.5px solid #d1d5db;
      border-radius: 4px; cursor: pointer; appearance: none;
      background: var(--surface); transition: all .12s; flex-shrink: 0; position: relative;
    }
    .audit-checkbox:checked { background: var(--accent); border-color: var(--accent); }
    .audit-checkbox:checked::after {
      content: ''; position: absolute; left: 3px; top: 1px;
      width: 5px; height: 8px; border: 1.5px solid #fff;
      border-top: none; border-left: none; transform: rotate(45deg);
    }

    /* ── Status badges ── */
    .audit-badge {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 2px 8px; border-radius: 20px; font-size: 10.5px;
      font-weight: 600; white-space: nowrap;
    }
    .audit-badge-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
    .audit-badge-pending  { background: var(--amber-2); color: var(--amber); border: 1px solid var(--amber-b); }
    .audit-badge-approved { background: var(--green-2); color: var(--green); border: 1px solid var(--green-b); }
    .audit-badge-rejected { background: var(--red-2);   color: var(--red);   border: 1px solid var(--red-b);   }
    .audit-badge-review   { background: var(--accent-3); color: var(--accent); border: 1px solid var(--accent-2); }

    /* ── Modal ── */
    .audit-modal-overlay {
      position: fixed; inset: 0; background: rgba(0,0,0,.4);
      backdrop-filter: blur(3px); z-index: 1000;
      display: flex; align-items: center; justify-content: center;
      padding: 20px; animation: auditFadeIn .15s ease;
    }
    .audit-modal-box {
      background: var(--surface); border-radius: 12px;
      box-shadow: 0 20px 60px rgba(0,0,0,.15);
      width: 100%; max-height: 90vh; overflow: hidden;
      display: flex; flex-direction: column;
      animation: auditScaleIn .18s ease;
    }

    /* ── Inline select ── */
    .audit-inline-select {
      padding: 6px 10px; border: 1px solid var(--rule); border-radius: 6px;
      font-size: 12.5px; font-family: var(--font-sans); font-weight: 500;
      color: var(--ink-2); background: var(--surface); cursor: pointer;
      outline: none; transition: all .15s;
    }
    .audit-inline-select:focus { border-color: var(--accent); }
    .audit-inline-select.active { border-color: var(--accent); background: var(--accent-3); color: var(--accent); font-weight: 600; }

    /* ── Table row hover ── */
    .audit-kpi-row { transition: background .1s; border-bottom: 1px solid var(--rule-2); }
    .audit-kpi-row:hover { background: #fafbff !important; }
    .audit-kpi-row:last-child { border-bottom: none; }

    /* ── Expanded panel ── */
    .audit-panel { border-top: 2px solid var(--accent-2); background: var(--surface-2); }

    /* ── Shimmer ── */
    .audit-shimmer {
      background: linear-gradient(90deg, #f3f4f6 25%, #e9eaec 50%, #f3f4f6 75%);
      background-size: 400% 100%; animation: auditShimmer 1.3s ease infinite;
    }

    /* ── Spinner ── */
    .audit-spinner {
      width: 18px; height: 18px; border: 2px solid var(--rule);
      border-top-color: var(--accent); border-radius: 50%;
      animation: auditSpin .65s linear infinite; flex-shrink: 0;
    }

    /* ── Bulk bar ── */
    .audit-bulk-bar { animation: auditSlideDown .18s ease; }

    /* ── Dropdown ── */
    .audit-dropdown-menu {
      position: absolute; top: calc(100% + 4px); right: 0;
      background: var(--surface); border: 1px solid var(--rule);
      border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,.1);
      z-index: 200; min-width: 180px; overflow: hidden;
      animation: auditSlideDown .13s ease;
    }
    .audit-dropdown-item {
      display: flex; align-items: center; gap: 8px;
      padding: 9px 13px; font-size: 12.5px; cursor: pointer;
      transition: background .1s; color: var(--ink-2); font-weight: 500;
    }
    .audit-dropdown-item:hover { background: var(--surface-2); }
    .audit-dropdown-item.danger { color: var(--red); }
    .audit-dropdown-item.danger:hover { background: var(--red-2); }

    /* ── Animations ── */
    @keyframes auditFadeIn    { from { opacity: 0; }                              to { opacity: 1; } }
    @keyframes auditScaleIn   { from { opacity: 0; transform: scale(.97); }       to { opacity: 1; transform: scale(1); } }
    @keyframes auditSlideDown { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes auditSlideUp   { from { opacity: 0; transform: translateY(5px); }  to { opacity: 1; transform: translateY(0); } }
    @keyframes auditSpin      { to { transform: rotate(360deg); } }
    @keyframes auditShimmer   { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
    .audit-slide-down { animation: auditSlideDown .2s ease; }
    .audit-slide-up   { animation: auditSlideUp .2s ease; }
  `;
  document.head.appendChild(s);
};